import type {
  FlightPlayerScoresFragment,
  HoleScoreFragment,
} from '../../../__generated__/hooks'
import type { CurrentPlayerCtx } from '../../../PlayerShell'
import { firstName } from '../helpers'
import HoleRow from './HoleRow'
import TotalRow from './TotalRow'

/**
 * The actual scorecard table — header (HULL · player columns) + body
 * (HoleRow per hole + TotalRow per side + grand total). Layout is
 * vertical (rows = holes, cols = players) so it fits a phone width
 * without horizontal scroll.
 *
 * The variant views (`IndividualView`, `BestBallView`) supply:
 *  - `flight` — which entries become columns (players or team-synth)
 *  - `cellSubLabel` — best-ball only; stamps contributor initials
 */
const Table = ({
  player,
  flight,
  holes,
  cellSubLabel,
  showStableford,
}: {
  player: CurrentPlayerCtx
  flight: FlightPlayerScoresFragment[]
  holes: HoleScoreFragment[]
  cellSubLabel?: (entryId: string, holeNumber: number) => string | null
  showStableford?: boolean
}) => {
  const isFull18 = holes.length >= 18
  const front = isFull18 ? holes.slice(0, 9) : holes
  const back = isFull18 ? holes.slice(9, 18) : []

  return (
    <div
      className="mx-2 rounded-lg shadow-sm overflow-hidden"
      style={{ background: 'var(--cream-card)' }}
    >
      <table
        className="w-full border-collapse"
        style={{ tableLayout: 'fixed' }}
      >
        <colgroup>
          <col style={{ width: 56 }} />
          {flight.map((p) => (
            <col key={p.id} />
          ))}
        </colgroup>
        <thead>
          <tr className="bg-green-900 text-paper">
            <th
              className="text-left px-2 py-2 text-[10px] font-bold"
              style={{ letterSpacing: 'var(--ls-caps)' }}
            >
              HULL
            </th>
            {flight.map((p) => {
              // Eyebrow: "Du" for the player's own row (Golf / BestBall
              // individual view), "Ditt lag" when the entry is the
              // player's team (Scramble or BestBall team view), no
              // eyebrow otherwise.
              const isYou = p.id === player.id
              const isYourTeam = !isYou && p.id === player.team?.id
              return (
                <th
                  key={p.id}
                  className="px-1 py-2 text-[11px] font-semibold text-center"
                >
                  <span className="block truncate">{firstName(p.name)}</span>
                  {isYou || isYourTeam ? (
                    <span className="block text-[8px] uppercase tracking-wider text-green-100 mt-0.5">
                      {isYou ? 'Du' : 'Ditt lag'}
                    </span>
                  ) : null}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {front.map((h) => (
            <HoleRow
              key={h.hole.number}
              hole={h}
              flight={flight}
              cellSubLabel={cellSubLabel}
              showStableford={showStableford}
            />
          ))}
          {isFull18 ? (
            <TotalRow
              label="Ut"
              flight={flight}
              holes={front}
              showStableford={showStableford}
            />
          ) : null}
          {back.map((h) => (
            <HoleRow
              key={h.hole.number}
              hole={h}
              flight={flight}
              cellSubLabel={cellSubLabel}
              showStableford={showStableford}
            />
          ))}
          {isFull18 ? (
            <TotalRow
              label="Inn"
              flight={flight}
              holes={back}
              showStableford={showStableford}
            />
          ) : null}
          <TotalRow
            label="Totalt"
            flight={flight}
            holes={holes}
            emphasized
            showStableford={showStableford}
          />
        </tbody>
      </table>
    </div>
  )
}

export default Table
